import {
  CSSProperties,
  RefObject,
  Suspense,
  SyntheticEvent,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { sortByOrder } from 'utils/collection-manipulation/array-sort-by';
import { EIconSize } from 'utils/constants/app/ui';
import { NOT_REAL_SPORTS } from 'utils/constants/sportsbook/sportsbook-data-hierarchy';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import { lazy } from 'utils/generic/lazy';
import { getMountPath, mountPoint } from 'utils/generic/route-manipulation';
import { isMobile } from 'utils/is-mobile';
import {
  Competition as CompetitionType,
  Game as GameType,
  Region
} from 'interfaces/sportsbook-data-levels';
import { Name, SpinWrapper, Wrapper } from './style';
import { EventCollapse } from 'components/EventCollapse';
import { GlobalIcon } from 'components/GlobalIcon';
import { Loading } from 'components/Loading';
import { UrlWrapper } from 'components/Shared/UrlWrapper';
import { CompetitionSkeleton } from 'components/UI/Skeletons/CompetitionSkeleton';
import { CompetitionsFavorite } from 'newcomponents/Shared/sportsbook/CompetitionsFavorite';
import { useSportsbookConfigs } from 'hooks/sportsbook/useSportsbookConfigs';
import { useGameScrollIntoView } from 'hooks/useGameScrollIntoView';
import { useSbRouteMatch } from 'newhooks/generic/useSbRouteMatch';
import { setScrollToCompetitionId, setTimeFilterData } from 'store/actions';
import {
  getCashedSport,
  getScrollToCompetitionId,
  getTimeFiltersData
} from 'store/selectors/sport-data';
import './index.less';

const { Game } = lazy(
  () => import(/* webpackChunkName: "sportsbook-live-game-card" */ './LiveGame')
);

type Props = {
  isDetailed: boolean;
  regionName: string;
  regionAlias: string;
  currentSport: string;
  sportAlias?: string;
  competitions: any;
  setListType?: Function;
  regionId?: number;
  regions?: Record<string, Region> | undefined;
  clickHandler?: Function;
  setCollapsedCount?: (arg: (num: number) => number) => void;
  count?: number;
  favorite?: boolean;
  fromSportsList?: boolean;
  isLive?: boolean;
  openFromSideBarPopup?: boolean;
  sportType?: number;
};

const USA_LAYOUT_HEIGHT = 85;
const TEAM2_ID_HEIGHT = 113;
const DEFAULT_HEIGHT = 64;

export const Competition = ({
  isDetailed,
  regionName,
  regionAlias,
  currentSport,
  sportAlias,
  competitions,
  setListType,
  clickHandler,
  setCollapsedCount,
  regions,
  count,
  favorite,
  fromSportsList,
  isLive,
  openFromSideBarPopup,
  sportType
}: Props) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const configs = useSportsbookConfigs();
  const { path } = useSbRouteMatch();
  const scrollToCompetitionId = useSelector(getScrollToCompetitionId);
  const cashedSport = useSelector(getCashedSport);
  const timeStamp = useSelector(getTimeFiltersData);

  const [USALayout] = useState(
    (SpringConfigs.IS_AMERICAN_LAYOUT || +configs.spbLayoutType === 1) &&
      isMobile()
  );

  const routeParams = useSbRouteMatch(
    `${mountPoint(window.getPathname())}/:sport?/:region?/:competition?/:game?`
  );

  const scrollToRef = useRef<HTMLDivElement>();
  const handleScroll = useGameScrollIntoView();

  const competitionsData = useMemo(() => {
    if (currentSport === 'outright') {
      return sortByOrder(Object.values(regions || {}))
        .map(region =>
          Object.values(region.competition || {}).map(comp => ({
            ...comp,
            region: region.alias
          }))
        )
        .flat()
        .filter(competition =>
          !USALayout || !isDetailed
            ? true
            : !!Object.keys(competition.game || {}).length
        );
    }

    return (
      sortByOrder(
        competitions instanceof Object ? Object.values(competitions) : [],
        currentSport === 'topLeague' ? 'favorite_order' : undefined
      ) as CompetitionType[]
    ).filter(competition =>
      !USALayout || !isDetailed
        ? true
        : !!Object.keys(competition.game || {}).length
    );
  }, [competitions, cashedSport, currentSport, regions]);

  const shouldScrollIntoView = useMemo(() => {
    return scrollToCompetitionId && !!competitions[scrollToCompetitionId?.id];
  }, [scrollToCompetitionId, competitionsData]);

  const handleOnWrapperClick = (
    e: SyntheticEvent,
    isNotRealSport: boolean,
    competition: CompetitionType<GameType>
  ) => {
    dispatch(
      setScrollToCompetitionId({
        id: competition?.id?.toString(),
        top: e.currentTarget.getBoundingClientRect().top
      })
    );
    window.scrollTo(0, 0);

    if (openFromSideBarPopup && clickHandler) {
      const pathname = `${getMountPath(
        routeParams?.path?.replace(
          /:sport\?.*$/,
          isNotRealSport ? `${currentSport}/${sportAlias}` : sportAlias || ''
        ) || ''
      )}/${isNotRealSport ? competition.region : regionAlias}/${
        competition.id
      }`;

      if (!document.querySelector('.prematch__type-page')) {
        clickHandler(
          `/${SpringConfigs.SPORTSBOOK_MOUNT_PATH}${
            isNotRealSport
              ? `/${currentSport}/${sportAlias}`
              : `/${sportAlias}` || ''
          }${pathname}`,
          sportAlias
        );
      } else {
        history.push(pathname);
      }
    } else {
      const redirectPath = `${getMountPath(path)}/${currentSport}${
        isNotRealSport ? `/${sportAlias}` : ''
      }/${isNotRealSport ? competition.region : regionAlias}/${competition.id}`;

      if (timeStamp && Object.keys(timeStamp).length) {
        dispatch(
          setTimeFilterData({
            option: 'All',
            date: false,
            count: 0,
            name: 'All Events'
          })
        );
      }

      typeof clickHandler === 'function'
        ? clickHandler(
            `/${SpringConfigs.SPORTSBOOK_MOUNT_PATH}${redirectPath}`,
            sportAlias
          )
        : history.push(redirectPath);
    }
  };

  useEffect(() => {
    shouldScrollIntoView &&
      scrollToRef.current &&
      handleScroll(scrollToRef.current, undefined, scrollToCompetitionId?.top);
  }, [shouldScrollIntoView]);

  if (!competitionsData) {
    return (
      <SpinWrapper>
        <Loading />
      </SpinWrapper>
    );
  }

  if (isDetailed) {
    return (
      <>
        {competitionsData.map((competition, index) => (
          <EventCollapse
            key={index}
            title={`${regionName}, ${competition.name}`}
            defaultActive={true}
            setCollapsedCount={setCollapsedCount}
            icon={
              <GlobalIcon lib="flags" name={regionAlias} size={EIconSize._24} />
            }
          >
            <div
              className="liveGame__wrapper"
              style={
                {
                  '--container-height':
                    !favorite &&
                    `${Object.values(
                      competition.game ||
                        ({} as Record<string, { team2_id: number }>)
                    ).reduce(
                      (acc, game) =>
                        acc +
                        (USALayout
                          ? USA_LAYOUT_HEIGHT
                          : game.team2_id
                          ? TEAM2_ID_HEIGHT
                          : DEFAULT_HEIGHT),
                      0
                    )}px`
                } as CSSProperties
              }
            >
              {
                <Suspense
                  fallback={
                    <CompetitionSkeleton
                      sportAlias={sportAlias}
                      count={count}
                    />
                  }
                >
                  <Game
                    regionName={regionName}
                    regionAlias={regionAlias}
                    currentSport={currentSport}
                    competition={competition}
                    setListType={setListType}
                    USALayout={USALayout}
                    favorite={favorite}
                    isLive={isLive}
                    sportType={sportType}
                  />
                </Suspense>
              }
            </div>
          </EventCollapse>
        ))}
      </>
    );
  }

  return (
    <>
      {competitionsData.map((competition, index) => {
        const isNotRealSport = NOT_REAL_SPORTS.includes(currentSport);

        return (
          <UrlWrapper
            key={index}
            url={`${getMountPath(path)}/${
              SpringConfigs.SPORTSBOOK_MOUNT_PATH
            }/${currentSport}${isNotRealSport ? `/${sportAlias}` : ''}/${
              isNotRealSport ? competition.region : regionAlias
            }/${competition.id}`}
            className={currentSport}
          >
            <Wrapper
              className="sportsbookListViewCompetiton__wrapper"
              onClick={e =>
                handleOnWrapperClick(e, isNotRealSport, competition)
              }
              {...(isMobile() &&
                scrollToCompetitionId?.id === competition?.id?.toString() && {
                  ref: scrollToRef as RefObject<HTMLDivElement>
                })}
            >
              {fromSportsList && (
                <CompetitionsFavorite
                  competitionInfo={{
                    id: competition.id,
                    name: competition.name,
                    regionAlias: competition.region || regionAlias,
                    sportAlias: isNotRealSport ? regionAlias : currentSport
                  }}
                />
              )}
              {isNotRealSport ? (
                <GlobalIcon
                  lib="flags"
                  name={competition.region || ''}
                  size={EIconSize._24}
                />
              ) : (
                <CompetitionsFavorite
                  competitionInfo={{
                    id: competition.id,
                    name: competition.name,
                    regionAlias: competition.region || regionAlias,
                    sportAlias: isNotRealSport ? regionAlias : currentSport
                  }}
                />
              )}
              <Name padding={!fromSportsList} className="competition__name">
                {competition.name}
              </Name>
              {isNotRealSport && !fromSportsList && (
                <CompetitionsFavorite
                  competitionInfo={{
                    id: competition.id,
                    name: competition.name,
                    regionAlias: competition.region || regionAlias,
                    sportAlias: isNotRealSport ? regionAlias : currentSport
                  }}
                />
              )}
            </Wrapper>
          </UrlWrapper>
        );
      })}
    </>
  );
};
